import {
  Input,
  Button,
  DatePicker,
  Select,
  Avatar,
  Upload,
  Row,
  Col,
  Form,
} from "antd";
import {
  UserOutlined,
  CameraOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import CreateEntityInfo from "../../../shared/CreateEntityInfo";
import dayjs from "dayjs";
import { Link, useParams } from "react-router-dom";

const { Option } = Select;
const cloudFrontKey = process.env.REACT_APP_CLOUDFRONT_KEY;

const EditUserScreen = (props: any) => {
  const {
    user,
    departments,
    positions,
    actions: { getDepartment, getPosition, getUserById, editUser },
  } = props;
  const [form] = Form.useForm();
  const [isChanged, setIsChanged] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [concatedDepartments, setConcatedDepartments] = useState<any>([]);
  const [concatedPositions, setConcatedPositions] = useState<any>([]);

  const { id } = useParams();

  useEffect(() => {
    getDepartment();
    getPosition();
    getUserById({ id });
  }, []);

  useEffect(() => {
    departments &&
      user?.assignments?.departments &&
      setConcatedDepartments(
        departments.concat(
          user?.assignments?.departments.filter(
            (item2: any) =>
              !departments.some((item1: any) => item1.id === item2.id)
          )
        )
      );
  }, [departments, user]);

  useEffect(() => {
    positions &&
      user?.assignments?.positions &&
      setConcatedPositions(
        positions.concat(
          user?.assignments?.positions.filter(
            (item2: any) =>
              !positions.some((item1: any) => item1.id === item2.id)
          )
        )
      );
  }, [positions, user]);

  useEffect(() => {
    if (!user) return;
    setAvatarUrl(
      user.profilePictureKey ? `${cloudFrontKey}/${user.profilePictureKey}` : ""
    );
    form.setFieldsValue({
      name: user.name || null,
      email: user.email,
      dateOfBirth: user.dateOfBirth ? dayjs(user.dateOfBirth) : null,
      department: user?.assignments?.departments.map((dep: any) => {
        const depIds = departments?.map((d: any) => d.id);
        return {
          value: dep.id,
          label: dep.name,
          disabled: depIds?.includes(dep?.id),
        };
      }),
      position: user?.assignments?.positions.map((pos: any) => {
        const posIds = positions?.map((p: any) => p.id);
        return {
          value: pos.id,
          label: pos.name,
          disabled: posIds?.includes(pos?.id),
        };
      }),
    });
  }, [user, form]);

  const handleFormChange = () => {
    const fieldsChanged = form.isFieldsTouched();
    setIsChanged(fieldsChanged);
  };

  const handleSaveChanges = () => {
    form
      .validateFields()
      .then((values) => {
        const formData = new FormData();
        if (!!values.name && !!values.name.trim())
          formData.append("name", values.name.trim());
        formData.append("email", values.email);
        if (values.dateOfBirth)
          formData.append(
            "dateOfBirth",
            values.dateOfBirth.format("YYYY-MM-DD")
          );

        values.department !== undefined
          ? values.department.length > 0
            ? values.department.forEach((department: any) => {
                formData.append(
                  "departmentIds",
                  department.value ?? department
                );
              })
            : formData.append("departmentIds", null as any)
          : formData.append("departmentIds", null as any);

        values.position !== undefined
          ? values.position.length > 0
            ? values.position.forEach((position: any) => {
                formData.append("positionIds", position.value ?? position);
              })
            : formData.append("positionIds", null as any)
          : formData.append("positionIds", null as any);

        if (profilePicture) {
          formData.append("profilePicture", profilePicture);
        }
        editUser({ id, user: formData });
        setIsChanged(false);
      })
      .catch((errorInfo) => {
        console.log("Failed:", errorInfo);
      });
  };

  const handleUploadChange = (info: any) => {
    const file = info.file.originFileObj || info.file;

    if (file) {
      setProfilePicture(file);
      try {
        const imageUrl = URL.createObjectURL(file);
        setAvatarUrl(imageUrl);
      } catch (error) {
        console.error("Error creating object URL: ", error);
      }

      setIsChanged(true);
    } else {
      console.error("No valid file provided for upload.");
    }
  };

  const validateDepartmentAndPosition = (_: any, value: any) => {
    const departmentId = form.getFieldValue("department");
    const positionId = form.getFieldValue("position");

    if (
      (!departmentId || departmentId.length === 0) &&
      (!positionId || positionId.length === 0)
    ) {
      return Promise.reject(
        new Error("At least one department or one position must be selected")
      );
    }

    Object.keys(form.getFieldsValue()).forEach((field) => {
      const error = form.getFieldError(field);
      if (!error.length) {
        return;
      }

      form.setFields([
        {
          name: field,
          errors: [],
        },
      ]);
    });
    return Promise.resolve();
  };

  const beforeUpload = (file: File) => {
    return false;
  };

  return (
    <Row className="h-full pb-[60px]">
      <Col lg={8}>
        <CreateEntityInfo
          title=""
          text={
            <Link
              to="/educator/user"
              className="text-neutral-600 font-semibold text-sm"
            >
              <ArrowLeftOutlined className="text-neutral-300 mr-2" /> Back to
              users
            </Link>
          }
        />
      </Col>
      <Col lg={16} className="h-full flex flex-col max-w-[600px]">
        {user && (
          <>
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Avatar size={150} icon={<UserOutlined />} src={avatarUrl} />
                <Upload
                  className="absolute bottom-0 right-0"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={handleUploadChange}
                  accept=".png,.jpg,.jpeg,.gif"
                >
                  <Button
                    shape="circle"
                    icon={<CameraOutlined />}
                    className="bg-blue-500 text-white"
                  />
                </Upload>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleFormChange}
              initialValues={{
                name: user.name,
                email: user.email,
                dateOfBirth: dayjs(user.dateOfBirth),
              }}
            >
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: !!user.name, message: "Please input user name!" },
                  {
                    validator: (_, value) => {
                      if (!!value && value.trim() === "") {
                        return Promise.reject(
                          new Error("Name cannot be whitespace!")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Name" size="large" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please input your email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input placeholder="Email" size="large" />
              </Form.Item>

              <Form.Item
                name="dateOfBirth"
                label="Date of Birth"
                rules={[
                  {
                    required: !!user.dateOfBirth,
                    message: "Please select your date of birth!",
                  },
                ]}
              >
                <DatePicker
                  placeholder="Date of Birth"
                  size="large"
                  className="w-full h-[50px]"
                  disabledDate={(current) =>
                    !current || current.isAfter(dayjs())
                  }
                  allowClear={false}
                />
              </Form.Item>
              {/*
              <Form.Item name="language" label="Language">
                <Select size="large" className="w-full min-h-[50px]">
                  <Option value="English">English</Option>
                  <Option value="Bosnian">Bosnian</Option>
                </Select>
              </Form.Item>
*/}
              <Form.Item
                name="department"
                label="Department"
                rules={[
                  { required: false },
                  { validator: validateDepartmentAndPosition },
                ]}
              >
                <Select
                  size="large"
                  className="w-full min-h-[50px]"
                  mode="multiple"
                  placeholder="Select Department"
                  options={concatedDepartments.map((concatedDep: any) => {
                    const depIds = departments?.map((d: any) => d.id);
                    return {
                      value: concatedDep.id,
                      label: concatedDep.name,
                      disabled: !depIds?.includes(concatedDep?.id),
                    };
                  })}
                ></Select>
              </Form.Item>

              <Form.Item
                name="position"
                label="Position"
                rules={[
                  { required: false },
                  { validator: validateDepartmentAndPosition },
                ]}
              >
                <Select
                  size="large"
                  className="w-full min-h-[50px]"
                  placeholder="Select Position"
                  mode="multiple"
                  options={concatedPositions.map((concatedPos: any) => {
                    const posIds = positions?.map((p: any) => p.id);
                    return {
                      value: concatedPos.id,
                      label: concatedPos.name,
                      disabled: !posIds?.includes(concatedPos?.id),
                    };
                  })}
                ></Select>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  block
                  size="large"
                  className="bg-primary-600 h-[50px]"
                  onClick={handleSaveChanges}
                  disabled={!isChanged}
                >
                  Save Changes
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Col>
    </Row>
  );
};

export default EditUserScreen;
